import { Badge, Button, DropdownMenu, Popover, Tooltip } from "@pej/kore/components";
import { Check, FolderInput, ImageOff, MoreVertical, Pencil, Trash2, TriangleAlert } from "lucide-react";
import { forwardRef, type KeyboardEvent, useMemo, useRef, useState } from "react";
import { ASSET_KIND } from "./asset-kind";
import type { AssetFolderSummary, AssetSummary, CreateFolderResult, FolderScopeValue, NameResult } from "./asset-types";
import { FolderPickList, type FolderPickRow } from "./folder-pick-list";
import { buildFolderRows } from "./folder-rows";
import { FontSpecimen } from "./font-specimen";
import { RenamePopover } from "./rename-popover";

export interface AssetTileGridProps {
    assets: AssetSummary[];
    columns: number;
    canManage: boolean;
    folders: AssetFolderSummary[];
    onSelect: (asset: AssetSummary) => void;
    /** "The user is considering this asset", or null on exit. Never commits. */
    onPreview: (asset: AssetSummary | null) => void;
    onMove: (asset: AssetSummary, folderId: string | null) => Promise<void>;
    onRenameAsset: (asset: AssetSummary, name: string) => Promise<NameResult>;
    onCreateFolder: (name: string) => Promise<CreateFolderResult>;
    /** Opens the confirmation, which the picker owns. Never deletes directly. */
    onDeleteRequest: (asset: AssetSummary) => void;
    inUseAssetId: string | null;
    /** Marks an asset as too small to print sharply in the slot this picker
     *  fills. Passed in (not computed) so this generic grid stays free of the
     *  print-resolution rules, which are the field's to know. */
    lowRes?: (asset: AssetSummary) => boolean;
}

// Move and rename are accepted only on a ready asset (spec §7). A rejected one
// can only be deleted; one still being verified can be neither, for the second
// or two that takes.
function isReady(asset: AssetSummary): boolean {
    return asset.state === "READY";
}

function stateLabel(asset: AssetSummary): string | null {
    if (asset.state === "READY") return null;
    if (asset.state === "REJECTED") return "Rejected";
    if (asset.state === "DELETING") return "Deleting";
    return "Checking";
}

/**
 * The picker's results grid. Four properties matter beyond rendering:
 *
 * - A hundred tiles must not become a hundred tab stops. This follows the APG
 *   roving-tabindex pattern: only the active tile is `tabIndex={0}`, the rest
 *   are `-1`, and arrow keys move the roving index and call `.focus()` directly.
 * - Selecting is never the same gesture as managing, for a mouse or a
 *   keyboard. A plain click selects; every management action lives behind the
 *   tile's own cog, inside a wrapper that stops click propagation so no click
 *   within it — the cog, a menu item, a folder row, the rename field's own
 *   buttons — ever reaches the tile's `onClick`. This has to be a wrapper, not
 *   just the cog's own handler: kore's DropdownMenu/Popover portal their
 *   content to `document.body`, and a portal still bubbles its events through
 *   the React *component* tree, not the DOM tree it renders into — so a click
 *   on a menu item or a folder row would otherwise reach this tile's
 *   `onClick` regardless. The same fact holds for keydown, so the tile's own
 *   `handleKeyDown` (its shortcuts for Enter/Space/Delete/arrows) guards
 *   itself with `event.target !== event.currentTarget` rather than a second
 *   wrapper: without it, Enter to submit a rename, or an ordinary Backspace
 *   or Space typed into the rename field or the move-to search box, would
 *   also fire this tile's own shortcuts.
 * - The cog is out of the roving sequence (`tabIndex={-1}`), so Shift+F10 and
 *   the context-menu key open the same menu — the platform-standard gesture,
 *   and the only keyboard route to it.
 * - The tile is a thumbnail frame with a caption row below it — name plus a
 *   `dimensions · folder` line — rather than the filename overlaid on the
 *   artwork (spec §B1). The 560px popover (up from 420px) is what absorbs the
 *   extra row height; see the width comment in `asset-picker.tsx`.
 */
export function AssetTileGrid({
    assets,
    columns,
    canManage,
    folders,
    onSelect,
    onPreview,
    onMove,
    onRenameAsset,
    onCreateFolder,
    onDeleteRequest,
    inUseAssetId,
    lowRes,
}: AssetTileGridProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const tileRefs = useRef<(HTMLDivElement | null)[]>([]);

    // `assets` is replaced wholesale on every search/scope change, so a stale
    // `activeIndex` from a longer list is frequent rather than an edge case —
    // clamped at read time so a shrink can never render a frame with zero
    // roving tab stops. An empty grid has no tile to be one; `-1` matches none.
    const activeTileIndex = assets.length === 0 ? -1 : Math.min(activeIndex, assets.length - 1);

    const moveRows = useMemo<FolderPickRow[]>(() => buildFolderRows(folders), [folders]);

    const moveTo = (index: number) => {
        setActiveIndex(index);
        tileRefs.current[index]?.focus();
    };

    return (
        <div
            role="listbox"
            aria-label="Assets"
            className="gap-2"
            style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
            // Preview's fourth unwind trigger (spec §8: "pointer exit, keyboard
            // navigation away, scroll and close") — the one with no
            // implementation until now. Tabbing out of the grid entirely (to
            // "Load more", to the footer) moves focus to an element outside
            // this container, so `relatedTarget` lands outside it too; moving
            // *within* the grid is handled by each tile's own onFocus below,
            // not here, so this only fires for the "left the grid" case.
            onBlur={(event) => {
                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                onPreview(null);
            }}
        >
            {assets.map((asset, index) => {
                const folderName = folders.find((folder) => folder.id === asset.folderId)?.name ?? "Unsorted";
                return (
                    <AssetTile
                        key={asset.id}
                        ref={(el) => {
                            tileRefs.current[index] = el;
                        }}
                        asset={asset}
                        canManage={canManage}
                        isActive={index === activeTileIndex}
                        inUse={asset.id === inUseAssetId}
                        moveRows={moveRows}
                        folderName={folderName}
                        onSelect={onSelect}
                        onPreview={onPreview}
                        onMove={onMove}
                        onRenameAsset={onRenameAsset}
                        onCreateFolder={onCreateFolder}
                        onDeleteRequest={onDeleteRequest}
                        lowRes={lowRes?.(asset) ?? false}
                        onFocusTile={() => setActiveIndex(index)}
                        onArrow={(key) => {
                            const last = assets.length - 1;
                            if (key === "ArrowRight") moveTo(Math.min(index + 1, last));
                            else if (key === "ArrowLeft") moveTo(Math.max(index - 1, 0));
                            else if (key === "ArrowDown") moveTo(Math.min(index + columns, last));
                            else if (key === "ArrowUp") moveTo(Math.max(index - columns, 0));
                            else if (key === "Home") moveTo(0);
                            else moveTo(last);
                        }}
                    />
                );
            })}
        </div>
    );
}

interface AssetTileProps {
    asset: AssetSummary;
    canManage: boolean;
    isActive: boolean;
    inUse: boolean;
    /** Too small to print sharply in this slot — surfaces a caption warning. */
    lowRes: boolean;
    moveRows: FolderPickRow[];
    folderName: string;
    onSelect: (asset: AssetSummary) => void;
    onPreview: (asset: AssetSummary | null) => void;
    onMove: (asset: AssetSummary, folderId: string | null) => Promise<void>;
    onRenameAsset: (asset: AssetSummary, name: string) => Promise<NameResult>;
    onCreateFolder: (name: string) => Promise<CreateFolderResult>;
    onDeleteRequest: (asset: AssetSummary) => void;
    onFocusTile: () => void;
    onArrow: (key: "ArrowRight" | "ArrowLeft" | "ArrowDown" | "ArrowUp" | "Home" | "End") => void;
}

const AssetTile = forwardRef<HTMLDivElement, AssetTileProps>(function AssetTile(
    {
        asset,
        canManage,
        isActive,
        inUse,
        lowRes,
        moveRows,
        folderName,
        onSelect,
        onPreview,
        onMove,
        onRenameAsset,
        onCreateFolder,
        onDeleteRequest,
        onFocusTile,
        onArrow,
    },
    ref,
) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const cogRef = useRef<HTMLButtonElement>(null);

    const ready = isReady(asset);
    const stateBadge = stateLabel(asset);
    // State outranks in-use: a non-ready tile shows its state pill and no
    // ring/check, no matter what `inUse` says (see the render block below).
    const showInUse = inUse && ready;
    const badgeTone: "danger" | "neutral" = asset.state === "REJECTED" ? "danger" : "neutral";
    // Computed once and shared with `aria-label` so the visible pill and the
    // accessible name can never disagree about which fact is showing.
    const suffix = [stateBadge, showInUse ? "In use here" : null].filter(Boolean).join(", ");
    const ariaLabel = suffix ? `${asset.displayName}, ${suffix}` : asset.displayName;
    const dimensions = asset.width != null && asset.height != null ? `${asset.width} × ${asset.height}` : null;
    const meta = dimensions ? `${dimensions} · ${folderName}` : folderName;

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        // The same portal-bubbling fact the click wrapper exists for (see the
        // docblock above) applies to every synthetic event type, keydown
        // included: a keydown that actually originated inside a portalled
        // descendant — a menu item, a folder row, the rename field, its Save
        // button — still reaches this handler via React's component-tree
        // bubbling, even though none of those elements are DOM descendants of
        // this tile. Without this guard, Enter to submit a rename would also
        // select (and close the picker on) this tile; Backspace edited into
        // the rename field or the move-to search box would also pop the
        // delete confirmation; a space typed while naming a new folder would
        // also select. Only handle these tile-level shortcuts when the
        // keydown's real target IS this tile — never a bubbled descendant.
        if (event.target !== event.currentTarget) return;
        switch (event.key) {
            case "ArrowRight":
            case "ArrowLeft":
            case "ArrowDown":
            case "ArrowUp":
            case "Home":
            case "End":
                onArrow(event.key);
                break;
            case "Enter":
            case " ":
                if (ready) onSelect(asset);
                break;
            // Shift+F10 and the context-menu key are the platform gesture for
            // "open this item's actions". The cog is tabIndex={-1} to stay out of
            // the roving sequence, so this is the only keyboard route to it.
            case "F10":
                if (!event.shiftKey) return;
                if (canManage) setMenuOpen(true);
                break;
            case "ContextMenu":
                // Unlike F10 above, there is no shift-key gate to fold this
                // check into — so without an explicit early return, a
                // read-only keyboard user reaches `event.preventDefault()`
                // below regardless, suppressing the browser's own context
                // menu while opening nothing of ours (deferred item 5, final
                // review).
                if (!canManage) return;
                setMenuOpen(true);
                break;
            case "Delete":
            case "Backspace":
                if (canManage) onDeleteRequest(asset);
                break;
            default:
                return;
        }
        event.preventDefault();
    };

    const handleMove = async (value: FolderScopeValue) => {
        setMoveOpen(false);
        await onMove(asset, value.mode === "folder" ? value.folderId : null);
    };

    return (
        <div
            ref={ref}
            role="option"
            // `aria-label` replaces the element's contents for name
            // computation, so a fact that only exists as a sibling `Badge` or
            // corner check below would otherwise be invisible to assistive
            // tech — folding it in here is what keeps "Rejected"/"Checking"/
            // "In use here" reachable at all (I2, final review).
            aria-label={ariaLabel}
            // `aria-selected` is a required property of role="option" in ARIA
            // 1.2. Tying it to `inUse` isn't a workaround to satisfy that
            // rule — in a picker listbox, "selected" and "in use by this
            // template" are the same fact.
            aria-selected={inUse}
            aria-disabled={!ready}
            tabIndex={isActive ? 0 : -1}
            // Known, accepted deviation: the cog below is a focusable element
            // nested inside this `option` leaf, which ARIA does not guarantee
            // assistive tech will expose. `aria-keyshortcuts` is what actually
            // carries the actions path to AT — the nested button is a pointer
            // convenience, not the contract.
            aria-keyshortcuts={canManage ? "Shift+F10 Delete" : undefined}
            onKeyDown={handleKeyDown}
            onFocus={() => {
                onFocusTile();
                // Unconditional, ahead of the `ready` check: mirrors
                // `onMouseLeave` firing null before the next `onMouseEnter`
                // no-ops. Without this, arrowing from a ready tile onto a
                // rejected one raises nothing (rejected tiles never preview),
                // so the previous tile's preview would stay stuck on an asset
                // nobody is considering (I1, final review, spec §8).
                onPreview(null);
                if (ready) onPreview(asset);
            }}
            onMouseEnter={() => {
                if (ready) onPreview(asset);
            }}
            onMouseLeave={() => onPreview(null)}
            onClick={() => {
                if (ready) onSelect(asset);
            }}
            className="group flex flex-col gap-1.5 rounded-kore-control outline-none focus-visible:ring-2 focus-visible:ring-kore-accent aria-disabled:cursor-not-allowed"
        >
            <div
                // A 2px green BORDER, not a ring: the tile's `absolute inset-0`
                // image sits over the padding box, so any inset ring/outline is
                // painted under it and never shows (that was the "border hardly
                // visible" bug). The border draws at the element edge, outside the
                // image, so it always reads. border-box keeps the outer size fixed,
                // so 2-vs-1px is no layout shift.
                className={`relative aspect-square overflow-hidden rounded-kore-control bg-kore-surface-tint ${
                    showInUse ? "border-2 border-status-ok-dot" : "border border-kore-border"
                }`}
            >
                {asset.type === "FONT" ? (
                    <FontSpecimen
                        url={asset.thumbnailUrl}
                        label={asset.displayName}
                        className="absolute inset-0 flex items-center justify-center text-3xl text-kore-text"
                    />
                ) : asset.thumbnailUrl ? (
                    <img src={asset.thumbnailUrl} alt="" className="absolute inset-0 size-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <ImageOff aria-hidden="true" className="size-5 text-kore-text-muted" />
                    </div>
                )}

                {/* State pill (Rejected/Checking/Deleting). Never overlaps the in-use
                    marker: showInUse requires `ready`, and a state pill only exists
                    while not ready — "this cannot be used" outranks "this is used". */}
                {stateBadge && (
                    <Badge tone={badgeTone} className="absolute top-1 left-1">
                        {stateBadge}
                    </Badge>
                )}

                {/* Ready-state corner badges: the in-use check, and a low-res
                    warning to its right, both filled circles in one top-left row.
                    Each requires `ready`, so neither ever coexists with the
                    not-ready state pill above.

                    Semantic fills, independent of the template's own brand accent:
                    `--status-ok-dot` is the "active" green ("active means green" —
                    app.css) — the cobalt accent already paints focus/hover/selection,
                    so it blended into that chrome; `--status-warn-dot` is the warn
                    amber. Both glyphs are `text-background` (near-white in light,
                    theme-aware) — NOT `kore-text-inverse`, which resolves to a
                    near-black in light mode. No text over the artwork (spec §B2): the
                    in-use fact rides aria-selected + the tile's aria-label, and the
                    low-res detail rides the tooltip. */}
                {ready && (showInUse || lowRes) && (
                    <div className="absolute top-1 left-1 flex gap-1">
                        {showInUse && (
                            <span
                                data-testid="in-use-check"
                                aria-hidden="true"
                                className="flex size-5 items-center justify-center rounded-full bg-status-ok-dot text-background"
                            >
                                <Check className="size-3" />
                            </span>
                        )}
                        {lowRes && (
                            <Tooltip>
                                <Tooltip.Trigger
                                    render={
                                        <span
                                            role="img"
                                            aria-label="Smaller than the print size"
                                            className="flex size-5 cursor-help items-center justify-center rounded-full bg-status-warn-dot text-background"
                                        >
                                            <TriangleAlert className="size-3" aria-hidden="true" />
                                        </span>
                                    }
                                />
                                <Tooltip.Content variant="inverse">
                                    Smaller than the print size — it may look soft on paper.
                                </Tooltip.Content>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* Every management surface below — the actions menu, the move popover,
                    the rename popover — portals its interactive content elsewhere in
                    the DOM (kore's DropdownMenu/Popover render into document.body), but
                    a portal still bubbles its events through the React *component*
                    tree, not the DOM tree it actually renders into. Without this
                    boundary, picking "Move to…", choosing a folder, or saving a rename
                    would each also reach this tile's own onClick below and silently
                    select — and, via the picker's onSelect, close the whole picker on
                    — the very asset being managed. Selecting and managing must never
                    be the same gesture (spec §4).
                    biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: pure
                    propagation boundary, not an interactive element — every actual
                    control inside (menu items, folder rows, the rename field and its
                    buttons) is its own focusable element with its own role and its own
                    keyboard handling; this div itself is never a target. */}
                <div onClick={(event) => event.stopPropagation()}>
                    {canManage && (
                        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                            <DropdownMenu.Trigger
                                ref={cogRef}
                                // tabIndex={-1}: a real focusable <button>, so without this a
                                // hundred tiles would put a hundred cogs in the tab sequence —
                                // exactly what the roving tabindex exists to prevent.
                                tabIndex={-1}
                                render={
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon-sm"
                                        aria-label={`Actions for ${asset.displayName}`}
                                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 data-[popup-open]:opacity-100"
                                    >
                                        <MoreVertical aria-hidden="true" />
                                    </Button>
                                }
                            />
                            <DropdownMenu.Content align="end">
                                {ready && (
                                    <DropdownMenu.Item onClick={() => setMoveOpen(true)}>
                                        <FolderInput aria-hidden="true" />
                                        Move to…
                                    </DropdownMenu.Item>
                                )}
                                {ready && (
                                    <DropdownMenu.Item onClick={() => setRenameOpen(true)}>
                                        <Pencil aria-hidden="true" />
                                        Rename
                                    </DropdownMenu.Item>
                                )}
                                {ready && <DropdownMenu.Separator />}
                                <DropdownMenu.Item variant="destructive" onClick={() => onDeleteRequest(asset)}>
                                    <Trash2 aria-hidden="true" />
                                    Delete
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    )}

                    {/* `canManage &&`, not just `moveOpen &&`: the cog above is
                        already gated on `canManage`, so `moveOpen` can only ever
                        become true while it was true — but a capability flip
                        landing while this popover happens to stay open would
                        otherwise leave a create-capable folder list mounted for
                        an operator who can no longer manage (C5, final review).
                        The enclosing guard on the DropdownMenu above is not
                        enough: this block is that guard's sibling, not its
                        child. */}
                    {canManage && moveOpen && (
                        <Popover open onOpenChange={setMoveOpen}>
                            <Popover.Content anchor={cogRef} align="end">
                                <FolderPickList
                                    rows={moveRows}
                                    selected={
                                        asset.folderId
                                            ? { mode: "folder", folderId: asset.folderId }
                                            : { mode: "unsorted" }
                                    }
                                    canCreate={canManage}
                                    title="Move to…"
                                    searchLabel="Move to folder"
                                    onChoose={(value) => void handleMove(value)}
                                    onCreate={async (name) => {
                                        const result = await onCreateFolder(name);
                                        // Create and move in one step, rather than making the
                                        // operator leave, create, and come back (spec §5.4).
                                        if (result.ok) await handleMove({ mode: "folder", folderId: result.folder.id });
                                        return result;
                                    }}
                                    onClose={() => setMoveOpen(false)}
                                />
                            </Popover.Content>
                        </Popover>
                    )}

                    <RenamePopover
                        open={renameOpen}
                        title={ASSET_KIND[asset.type].renameTitle}
                        initialValue={asset.displayName}
                        anchor={cogRef}
                        onSubmit={(name) => onRenameAsset(asset, name)}
                        onOpenChange={setRenameOpen}
                    />
                </div>
            </div>

            <div className="min-w-0 px-0.5">
                <p title={asset.displayName} className="truncate text-left text-kore-sm text-kore-text">
                    {asset.displayName}
                </p>
                <p className="truncate text-left text-kore-xs text-kore-text-muted">{meta}</p>
            </div>
        </div>
    );
});
